import { Box, Typography, Button, Paper } from '@mui/material';
import { createContext, useContext, useState } from 'react';
import { connect } from 'react-redux';
import { MESSAGE_TYPE, DEFAULT_MESSAGE, DARK_MODE } from '../../constants';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useResolutionScale } from '../../hooks';
import VerifiedIcon from '../../assets/icons/VerifiedIcon';
import ErrorRetryIcon from '../../assets/icons/ErrorRetryIcon';
import DialogBackground from '../../assets/icons/DialogBackground';
import CloseIcon from '@mui/icons-material/Close';
import SkipSquareIcon from '../../assets/icons/SkipSquareIcon';
import MessageButton from '../MessageButton/MessageButton';

export const MessageContext = createContext(null);

const MessageProvider = ({ children, themeMode, user }) => {
	const [popupType, setPopupType] = useState(MESSAGE_TYPE.INFORMATION);
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [messageData, setMessageData] = useState({
		contextText: null,
		info: null,
	});

	const openPopup = () => setIsPopupOpen(true);
	const closePopup = () => setIsPopupOpen(false);

	const clearStorage = () => {
		localStorage.clear();
		setIsPopupOpen(false);
		showMessageOverlay({
			contextText: 'Data has been cleared successfully!',
			info: '  ',
			success: true,
			troubleSuccess: true,
		});
		setTimeout(() => {
			setIsPopupOpen(false);
		}, 2000);
	};

	const setErrorMessageContext = (contextText, info, render) => {
		setMessageData({
			contextText: contextText ?? DEFAULT_MESSAGE.ERROR,
			info: info !== undefined ? info : (contextText ? '' : DEFAULT_MESSAGE.ERROR),
			render: render ?? null,
		});
		setPopupType(MESSAGE_TYPE.ERROR);
	};

	const setLoadingMessageContext = (contextText) => {
		setMessageData({
			contextText: contextText ?? DEFAULT_MESSAGE.ERROR,
		});
		setPopupType(MESSAGE_TYPE.LOADING);
	};

	const setSuccessMessageContext = (
		contextText,
		info,
		render,
		troubleSuccess,
	) => {
		setMessageData({
			contextText: contextText ?? DEFAULT_MESSAGE.SUCCESS,
			info: info !== undefined ? info : (contextText ? '' : DEFAULT_MESSAGE.SUCCESS),
			troubleSuccess: troubleSuccess ?? false,
			render: render ?? null,
		});
		setPopupType(MESSAGE_TYPE.SUCCESS);
	};

	const setTroubleMessageContext = (contextText, info, render) => {
		setMessageData({
			contextText: contextText ?? DEFAULT_MESSAGE.SUCCESS,
			info: info !== undefined ? info : (contextText ? '' : DEFAULT_MESSAGE.SUCCESS),
			render: render ?? null,
		});
		setPopupType(MESSAGE_TYPE.TROUBLE);
	};

	const showPopup = (args) => {
		if (args?.error) {
			setErrorMessageContext(args.contextText, args.info, args.render);
		} else if (args?.loading) {
			setLoadingMessageContext(args.contextText);
		} else if (args?.success) {
			setSuccessMessageContext(
				args.contextText,
				args.info,
				args.render,
				args.troubleSuccess,
			);
		} else if (args?.trouble) {
			setTroubleMessageContext(args.contextText, args.info, args.render);
		} else {
			setMessageData({
				contextText: DEFAULT_MESSAGE.ERROR,
				info: '',
			});
			setPopupType(MESSAGE_TYPE.LOADING);
		}
		openPopup();
	};

	const showMessageOverlay = (args) => {
		showPopup({ ...args });
		return closePopup;
	};

	return (
		<>
			<MessageOverlay
				open={isPopupOpen}
				handleClose={closePopup}
				handleStorage={clearStorage}
				messageData={messageData}
				type={popupType}
				themeMode={themeMode}
				user={user}
			/>
			<MessageContext.Provider
				value={{ isPopupOpen, showPopup, showMessageOverlay }}
			>
				{children}
			</MessageContext.Provider>
		</>
	);
};

export const useMessageContextManager = () => useContext(MessageContext);

const MessageOverlay = ({
	open,
	type,
	handleClose,
	handleStorage,
	messageData,
	themeMode,
	user,
}) => {
	const isDarkMode = themeMode === DARK_MODE;

	const { scaleValue } = useResolutionScale();
	if (!open) return null;
	const isSuccess = type === MESSAGE_TYPE.SUCCESS;
	const isError = type === MESSAGE_TYPE.ERROR;
	const isLoading = type === MESSAGE_TYPE.LOADING;
	const isTrouble = type === MESSAGE_TYPE.TROUBLE;

	// 🎨 Shared Color Tokens
	const palette = {
		light: {
			textPrimary: '#202020',
			textSecondary: '#0F107B',
			background: '#F2F2F2',
			surface: '#FFFFFF',
			border: 'rgba(45,74,45,0.3)',
			icon: '#202020',
		},
		dark: {
			textPrimary: '#E0E0E0',
			textSecondary: '#8082FF',
			background: '#1B1B1B',
			surface: '#2C2C2C',
			border: 'rgba(255,255,255,0.3)',
			icon: '#FFFFFF',
		},
		states: {
			success: {
				light: { base: '#1B681B', accent: '#08EE2B' },
				dark: { base: '#4CAF50', accent: '#00E676' }, // 🌿 Softer neon-green for dark mode
			},
			error: {
				light: { base: '#CB1D49', accent: '#650E24' },
				dark: { base: '#FF4C6A', accent: '#C2185B' }, // 🔴 Bright magenta-red for contrast
			},
			trouble: {
				light: { base: '#FF8C00', accent: '#FF7A00' },
				dark: { base: '#FFA733', accent: '#FF9800' }, // 🟠 Warm orange for dark mode
			},
		},
	};

	// 🧠 Select state colors dynamically
	const stateGroup = isSuccess
		? palette.states.success
		: isError
		? palette.states.error
		: isTrouble
		? palette.states.trouble
		: palette.states.success;

	const stateColors = isDarkMode ? stateGroup.dark : stateGroup.light;

	const themeColor = stateColors.base;
	const buttonGradient = isError
		? 'linear-gradient(135deg, #dc3545, #c82333)'
		: `linear-gradient(90deg, ${stateColors.accent} 0%, ${stateColors.base} 100%)`;
	const titleColor = isError
		? isDarkMode
			? palette.states.error.dark.base
			: palette.states.error.light.base
		: isTrouble
		? isDarkMode
			? palette.states.trouble.dark.base
			: palette.states.trouble.light.base
		: isSuccess
		? isDarkMode
			? palette.states.success.dark.base
			: palette.states.success.light.base
		: isDarkMode
		? palette.dark.textSecondary
		: palette.light.textSecondary;

	const barRoundedCornerSize = scaleValue(8);
	const messsageFontSize = scaleValue(26);

	return (
		<Box
			sx={{
				position: 'fixed',
				top: 0,
				left: 0,
				width: '100vw',
				height: '100vh',
				zIndex: 999999,
				background: 'rgba(0,0,0,0.6)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backdropFilter: isDarkMode ? 'blur(8px)' : 'none',
			}}
		>
			<Paper
				elevation={0}
				sx={{
					position: 'relative',
					width: `${scaleValue(650)}px`,
					height: `${scaleValue(368)}px`,
					borderRadius: `${scaleValue(48)}px`,
					background: isDarkMode
						? palette.dark.background
						: palette.light.background,
					color: isDarkMode
						? palette.dark.textPrimary
						: palette.light.textPrimary,
					overflow: 'hidden',
					boxShadow: isDarkMode
						? '0 0 20px rgba(255,255,255,0.05)'
						: '0 0 20px rgba(32, 12, 12, 0.1)',
				}}
			>
				{/* Side Bars */}
				{['left', 'right'].map((side) => (
					<Box
						key={side}
						sx={{
							position: 'absolute',
							top: '50%',
							[side]: 0,
							transform: 'translateY(-50%)',
							width: `${scaleValue(12.5)}px`,
							height: `${scaleValue(253)}px`,
							background: themeColor,
							borderRadius:
								side === 'left'
									? `0 ${barRoundedCornerSize}px ${barRoundedCornerSize}px 0`
									: `${barRoundedCornerSize}px 0 0 ${barRoundedCornerSize}px`,
							zIndex: 1,
							opacity: isDarkMode ? 0.7 : 1,
						}}
					/>
				))}

				{/* Background Wave */}
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						zIndex: 0,
						pointerEvents: 'none',
						overflow: 'hidden', // keep it clipped to Paper shape
						display: 'flex',
						justifyContent: 'flex-end', // ✅ align horizontally to right
						alignItems: 'flex-end', // ✅ align vertically to bottom
					}}
				>
					<DialogBackground
						style={{
							width: '90%',
							height: '90%',
							objectFit: 'cover',
							objectPosition: 'right bottom',
							display: 'block',
							opacity: isDarkMode ? 0.15 : 1.0,
							transform: 'translate(6%, 6%)',
							filter: isDarkMode ? 'brightness(0.7)' : 'none',
						}}
					/>
				</Box>

				{/* ❌ Close Icon */}
				<Box
					onClick={handleClose}
					sx={{
						position: 'absolute',
						top: scaleValue(20),
						right: scaleValue(40),
						cursor: 'pointer',
						color: isDarkMode ? palette.dark.icon : palette.light.icon,
						zIndex: 10,
						'&:hover': { opacity: 0.7 },
					}}
				>
					<CloseIcon sx={{ fontSize: scaleValue(48) }} />
				</Box>

				{/* Center Content */}
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: `${scaleValue(18)}px`,
						textAlign: 'center',
						px: `${scaleValue(36)}px`,
						color: isDarkMode
							? palette.dark.textPrimary
							: palette.light.textPrimary,
					}}
				>
					{isLoading ? (
						<>
							<LoadingSpinner variant="minimal" size={scaleValue(80)} />
							<Typography
								variant="inherit"
								sx={{
									fontFamily: 'JLR Emeric',
									fontWeight: 600,
									fontSize: `${messsageFontSize}px`,
									color: isDarkMode
										? palette.dark.textSecondary
										: palette.light.textSecondary,
								}}
							>
								{messageData.contextText}
							</Typography>
						</>
					) : (
						<>
							{/* Icon Placeholder */}
							<Box
								sx={{
									width: `${scaleValue(105)}px`,
									height: `${scaleValue(58)}px`,
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
								}}
							>
								{isSuccess ? (
									<VerifiedIcon
										style={{ width: scaleValue(58), height: scaleValue(58) }}
									/>
								) : isTrouble ? (
									<SkipSquareIcon
										style={{ width: scaleValue(58), height: scaleValue(58) }}
									/>
								) : (
									<ErrorRetryIcon
										style={{ width: scaleValue(58), height: scaleValue(58) }}
									/>
								)}
							</Box>

							{/* Title */}
							<Typography
								variant="inherit"
								sx={{
									fontFamily: 'JLR Emeric',
									fontWeight: 600,
									fontSize: `${messsageFontSize}px`,
									color: titleColor,
								}}
							>
								{messageData.contextText}
							</Typography>

							{/* Subtitle */}
							{Boolean(messageData.info) && (
								<Typography
									variant="inherit"
									sx={{
										fontFamily: 'JLR Emeric',
										fontWeight: 400,
										fontSize: `${messsageFontSize}px`,
										color: isDarkMode
											? palette.dark.textSecondary
											: palette.light.textSecondary,
										lineHeight: 1.3,
									}}
								>
									{messageData.info}
								</Typography>
							)}

							{/* 🔘 Action Buttons */}
							<Box
								sx={{
									display: 'flex',
									gap: scaleValue(8),
									justifyContent: 'center',
									alignItems: 'center',
									flexWrap: 'wrap',
								}}
							>
								{messageData.render ? (
									// 🧩 Custom UI (render function or React node)
									typeof messageData.render === 'function' ? (
										messageData.render({
											close: handleClose,
											clearStorage: handleStorage,
											isDarkMode,
											palette,
											buttonGradient,
											messageData,
											type,
											themeMode,
										})
									) : (
										messageData.render
									)
								) : (
									<>
										{/* ❌ ERROR STATE BUTTONS */}
										{isError && (
											<>
												<MessageButton
													variant="outlined"
													label="Clear Cache"
													onClick={handleStorage}
													themeMode={themeMode}
													palette={palette}
													buttonGradient={buttonGradient}
												/>
												<MessageButton
													variant="contained"
													label="Close"
													onClick={handleClose}
													themeMode={themeMode}
													palette={palette}
													buttonGradient={buttonGradient}
												/>
											</>
										)}

										{/* ✅ SUCCESS & ⚠️ TROUBLE STATES */}
										{(isSuccess || isTrouble) &&
											!messageData.troubleSuccess && (
												<MessageButton
													variant="contained"
													label={isSuccess ? 'Continue' : 'Close'}
													onClick={handleClose}
													themeMode={themeMode}
													palette={palette}
													buttonGradient={buttonGradient}
												/>
											)}
									</>
								)}
							</Box>
						</>
					)}
				</Box>
			</Paper>
		</Box>
	);
};

const mapStateToProps = (state) => ({
	themeMode: state.theme.themeMode,
	user: state.user,
});

export default connect(mapStateToProps)(MessageProvider);
